import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ElaLogo from "@/components/brand/ElaLogo";
import EliaAdvisorFAB from "@/components/EliaAdvisorFAB";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import OfflineIndicator from "@/components/OfflineIndicator";
import ProfileMenu from "@/components/ProfileMenu";
import { ImpersonationBanner } from "@/components/Impersonation";
import ThemeToggle from "@/components/ThemeToggle";
import {
  LayoutDashboard,
  Sparkles,
  FolderOpen,
  Files,
  MessageSquareText,
  Building2,
  LogOut,
  MapPin,
  FileDown,
  ScanLine,
  Handshake,
  History,
  Zap,
  Calendar,
  Target,
  Crown,
  ShieldCheck,
  BookOpen,
  ClipboardList,
  FileText,
  Gauge,
  Users,
  Gavel,
  User2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/lib/featureFlags";
import { initials } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const NAV = [
// Navigation items — chaque entrée peut porter un `flag` : le lien n'apparaît
// que si le feature flag est actif OU si le champ est absent.
// Objectif MVP V1 : parcours unique (Dossier · Documents · Assistant ELIA · Dashboard).
];

const NAV_ITEMS_ALL = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", emoji: "📊" },
  { to: "/moi", label: "Mon dossier", icon: User2, testid: "nav-moi", emoji: "👤" },
  { to: "/documents", label: "Coffre", icon: Files, testid: "nav-documents" },
  { to: "/assistant", label: "Assistante ELIA", icon: MessageSquareText, testid: "nav-assistant" },
  { to: "/dossiers", label: "Générer un dossier", icon: FileDown, testid: "nav-dossiers", emoji: "📁" },
  { to: "/smart-docs", label: "Docs intelligents", icon: ScanLine, testid: "nav-smart-docs", emoji: "🧠", flag: "document_ocr" },
  { to: "/geolocation", label: "Géolocalisation", icon: MapPin, testid: "nav-geo", flag: "geolocation" },
  { to: "/plans", label: "Plans", icon: Crown, testid: "nav-plans" },
  { to: "/security", label: "Sécurité", icon: ShieldCheck, testid: "nav-security" },
  // ── Liens institutionnels — masqués tant que le flag est OFF ───────
  { to: "/altiora", label: "ALTIORA détail", icon: Sparkles, testid: "nav-altiora", flag: "detailed_financial_analysis" },
  { to: "/projects", label: "Mes projets", icon: FolderOpen, testid: "nav-projects", flag: "dashboard_projects_insight" },
  { to: "/copilot", label: "Copilote", icon: Zap, testid: "nav-copilot", flag: "full_analyst_report" },
  { to: "/agenda", label: "Agenda", icon: Calendar, testid: "nav-agenda", flag: "full_analyst_report" },
  { to: "/opportunities", label: "Opportunités", icon: Target, testid: "nav-opportunities", flag: "dashboard_partner_recos" },
  { to: "/timeline", label: "Journal de vie", icon: History, testid: "nav-timeline", flag: "dashboard_activity_feed" },
  { to: "/partenaires", label: "Réseau ELIA", icon: Handshake, testid: "nav-partners", flag: "dashboard_partner_recos" },
  { to: "/knowledge", label: "Documentation", icon: BookOpen, testid: "nav-knowledge", flag: "full_analyst_report" },
  { to: "/collection", label: "Collecte", icon: ClipboardList, testid: "nav-collection", flag: "full_analyst_report" },
  { to: "/output", label: "Exports", icon: FileText, testid: "nav-output", flag: "full_analyst_report" },
  // Admin — visible uniquement pour super_founder (validation runtime)
  { to: "/admin/feature-flags", label: "Feature Flags", icon: Sparkles, testid: "nav-ff-admin", requiresSuperFounder: true },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();

  // Filtre : un lien sans `flag` est toujours visible ;
  // un lien avec `flag` n'apparaît que si le flag est actif.
  // Un lien avec `requiresSuperFounder` n'apparaît que pour le fondateur.
  const isSuperFounder = user?.role === "super_founder" || user?.role === "founder";
  const navItems = NAV_ITEMS_ALL.filter((item) => {
    if (item.requiresSuperFounder && !isSuperFounder) return false;
    if (item.flag && !isEnabled(item.flag)) return false;
    return true;
  });

  const isConseiller = user?.role === "conseiller" || user?.role === "admin";
  const isPartnerAdmin = user?.role === "partner_admin" || user?.role === "admin";
  const isAdmin = user?.role === "admin" || user?.role === "conseiller" || user?.role === "partner_admin";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-72 shrink-0 flex-col border-r border-border/60 bg-background/50 backdrop-blur-xl relative z-10">
        <div className="px-6 py-6">
          <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="brand-link">
            <ElaLogo variant="horizontal" className="h-9" />
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground -ml-1">Finance</div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid, emoji }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-foreground/70 hover:bg-muted"
                }`
              }
            >
              {emoji ? (
                <span className="text-base leading-none" aria-hidden>{emoji}</span>
              ) : (
                <Icon className="w-4 h-4" />
              )}
              <span>{label}</span>
            </NavLink>
          ))}

          {isConseiller && (
            <NavLink
              to="/institution"
              data-testid="nav-institution"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors mt-6 border-t border-border/60 pt-4 ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-foreground/70 hover:bg-muted"
                }`
              }
            >
              <Building2 className="w-4 h-4" />
              <span>Espace Institution</span>
            </NavLink>
          )}

          {isPartnerAdmin && (
            <>
              <NavLink
                to="/hub"
                data-testid="nav-hub"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors mt-6 border-t border-border/60 pt-4 ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <Building2 className="w-4 h-4" />
                <span>Partner Hub</span>
              </NavLink>
              <NavLink
                to="/foliomap"
                data-testid="nav-foliomap"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <Handshake className="w-4 h-4" />
                <span className="flex-1">FolioMap</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">bientôt</span>
              </NavLink>
            </>
          )}

          {isAdmin && (
            <div className="mt-6 border-t border-border/60 pt-4 space-y-1">
              <div className="px-4 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Centres opérationnels
              </div>
              <NavLink
                to="/admin/executive"
                data-testid="nav-admin-executive"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <Gauge className="w-4 h-4" />
                <span>Tableau de bord DG</span>
              </NavLink>
              <NavLink
                to="/admin/portal"
                data-testid="nav-admin-portal"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <Users className="w-4 h-4" />
                <span>Portail Admin</span>
              </NavLink>
              <NavLink
                to="/admin/audit"
                data-testid="nav-admin-audit"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Centre d&apos;Audit</span>
              </NavLink>
              <NavLink
                to="/admin/committee"
                data-testid="nav-admin-committee"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <Gavel className="w-4 h-4" />
                <span>Salle de comité</span>
              </NavLink>
              <NavLink
                to="/admin/support"
                data-testid="nav-admin-support"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <BookOpen className="w-4 h-4" />
                <span>Support & Formation</span>
              </NavLink>
              <NavLink
                to="/admin/config"
                data-testid="nav-admin-config"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted"
                  }`
                }
              >
                <ClipboardList className="w-4 h-4" />
                <span>Configuration</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-border/60 space-y-3">
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" className="flex-1" />
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border border-border/60">
              {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                {initials(user?.name || user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="rounded-full"
              data-testid="sidebar-logout-btn"
              title="Se déconnecter"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative z-10">
        {/* Topbar global — avatar + profile menu, visible sur toutes les pages
            desktop et mobile. Sticky en haut à droite. */}
        <div
          className="sticky top-0 z-30 flex items-center justify-end gap-3 px-4 md:px-8 py-3 bg-background/70 backdrop-blur-md border-b border-border/40"
          data-testid="app-topbar"
        >
          <ProfileMenu />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="px-4 sm:px-6 md:px-12 py-8 md:py-12 max-w-6xl mx-auto"
        >
          {children}
        </motion.div>
      </main>
      <EliaAdvisorFAB />
      <OfflineIndicator />
      </div>
    </div>
  );
}
